/**
 * joyeria-precio.js
 * Web Component para el configurador de precio dinámico de joyería.
 *
 * Arquitectura: Plugin & Play — sin dependencias externas.
 * Los datos provienen de Metaobjects via Liquid (data-componentes, etc).
 *
 * Atributos del elemento:
 *   data-componentes          {string}  JSON array: [{nombre, gramos, rates: {10k,14k,18k,24k}}]
 *   data-precio-piedras       {number}  Precio fijo de piedras (no varía con quilate)
 *   data-quilates-disponibles {string}  JSON array: ['14k','18k']
 *   data-quilate-default      {string}  Quilate inicial: '10k'|'14k'|'18k'|'24k'
 *   data-money-format         {string}  shop.money_format de Shopify
 *   data-form-id              {string}  ID del product form para line item properties
 *
 * Elementos internos controlados:
 *   [data-joyeria-quilates]    → <input type="radio"> para selección de quilate
 *   [data-joyeria-precio]      → Span del precio visible
 *   [data-joyeria-schema]      → <script type="application/ld+json"> para SEO
 *   [data-joyeria-quilate-desc] → Span accesible con quilate actual
 */

class JoyeriaConfigurador extends HTMLElement {
  connectedCallback() {
    this._componentes    = this._parseJSON(this.dataset.componentes, []);
    this._precioPiedras  = parseFloat(this.dataset.precioPiedras || '0');
    this._quilatesDisp   = this._parseJSON(this.dataset.quilatesDisponibles, []);
    this._quilateDefault = this.dataset.quilateDefault || '18k';
    this._moneyFormat    = this.dataset.moneyFormat || '${{amount}}';
    this._formId         = this.dataset.formId || '';

    // Escuchar cambio de quilate (radio buttons)
    this.querySelectorAll('[data-joyeria-quilates]')
      .forEach(el => el.addEventListener('change', e => this._actualizar(e.target.value)));

    // Inicializar con quilate default
    this._actualizar(this._quilateDefault);
  }

  /**
   * Recalcula y actualiza el precio al cambiar el quilate seleccionado.
   * @param {string} quilate - '10k' | '14k' | '18k' | '24k'
   */
  _actualizar(quilate) {
    // 1. Calcular total de metales
    let totalMetales = 0;
    for (const comp of this._componentes) {
      const tasa = (comp.rates && comp.rates[quilate]) ? parseFloat(comp.rates[quilate]) : 0;
      totalMetales += parseFloat(comp.gramos || 0) * tasa;
    }

    const total    = totalMetales + this._precioPiedras;
    const centavos = Math.round(total * 100);

    // 2. Actualizar precio visible
    const priceEl = this.querySelector('[data-joyeria-precio]');
    if (priceEl) {
      priceEl.textContent          = this._formatMoney(total);
      priceEl.dataset.joyeriaPrice = centavos;
    }

    // 3. Actualizar JSON-LD schema.org (para SEO dinámico)
    const schemaEl = this.querySelector('[data-joyeria-schema]');
    if (schemaEl) {
      try {
        const data = JSON.parse(schemaEl.textContent);
        data.price = parseFloat(total.toFixed(2));
        schemaEl.textContent = JSON.stringify(data);
      } catch (_) {
        // JSON-LD malformado — no bloquear UX
      }
    }

    // 4. Actualizar descripción accesible
    const descEl = this.querySelector('[data-joyeria-quilate-desc]');
    if (descEl) descEl.textContent = `${quilate} seleccionado`;

    // 5. Actualizar line item properties
    this._actualizarLineItemProperties(quilate, total);
  }

  /**
   * Inyecta / actualiza los hidden inputs de line item properties en el product form.
   * Prefijo _ oculta las propiedades al cliente pero las muestra al admin.
   * @param {string} quilate
   * @param {number} total - precio en unidades (no centavos)
   */
  _actualizarLineItemProperties(quilate, total) {
    if (!this._formId) return;

    const form = document.getElementById(this._formId);
    if (!form) return;

    const centavos = Math.round(total * 100);
    this._setHiddenInput(form, 'properties[_quilate]', quilate);
    this._setHiddenInput(form, 'properties[_composicion]', this._generarComposicion(quilate));
    this._setHiddenInput(form, 'properties[_precio_calculado]', this._formatMoney(total));
    this._setHiddenInput(form, 'properties[_precio_centavos]', String(centavos));
  }

  /**
   * Crea o actualiza un input hidden dentro del form.
   * @param {HTMLElement} form
   * @param {string} name
   * @param {string} value
   */
  _setHiddenInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input          = document.createElement('input');
      input.type     = 'hidden';
      input.name     = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  /**
   * Genera un string de composición descriptivo para el admin.
   * Ejemplo: "Oro Italiano 3.5g · Diamante 0.2ct"
   * @param {string} quilate
   * @returns {string}
   */
  _generarComposicion(quilate) {
    const partes = this._componentes.map(comp => {
      const gramos = parseFloat(comp.gramos || 0);
      return `${comp.nombre || 'Metal'} ${gramos}g (${quilate})`;
    });
    return partes.join(' · ');
  }

  /**
   * Formatea un monto usando el money_format de Shopify.
   * Soporta todos los formatos nativos de Shopify.
   * @param {number} amount - Precio en unidades (no centavos)
   * @returns {string}
   */
  _formatMoney(amount) {
    const fmt     = this._moneyFormat;
    const rounded = Math.round(amount);
    const fixed2  = amount.toFixed(2);

    // Separador de miles (puntos para formato latinoamericano)
    const withThousands = rounded
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return fmt
      .replace('{{amount_with_comma_separator}}', fixed2.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', withThousands)
      .replace('{{amount_no_decimals}}', rounded.toString())
      .replace('{{amount}}', fixed2);
  }

  /**
   * Parsea JSON de forma segura con fallback.
   * @param {string} str
   * @param {*} fallback
   * @returns {*}
   */
  _parseJSON(str, fallback) {
    if (!str) return fallback;
    try {
      return JSON.parse(str);
    } catch (_) {
      return fallback;
    }
  }
}

if (!customElements.get('joyeria-configurador')) {
  customElements.define('joyeria-configurador', JoyeriaConfigurador);
}
